import localforage from 'localforage'

const KEY_SEPARATOR = '|'

const toHex = (buffer) => Array.from(new Uint8Array(buffer))
  .map((byte) => byte.toString(16).padStart(2, '0'))
  .join('')

const digestScope = async (scope) => {
  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(String(scope)))
  return toHex(digest)
}

/**
 * SimpleCache tier-2 adapter shared by every same-origin PowerPoint add-in frame: each frame is its
 * own JS heap, so an IndexedDB copy lets a frame resolve data a sibling frame already fetched.
 * Records are namespaced by schema version and a digest of the scope (the session identity), so
 * neither an incompatible shape nor another user ever reads them. How long a copy may be served is
 * the caller's per-entry `persistent.maxAgeInSeconds`.
 *
 * @param {object} options
 * @param {{getItem: Function, setItem: Function, removeItem: Function}} options.storage localforage-shaped
 * @param {() => boolean} options.isEnabled a closed gate reads and writes nothing
 * @param {string} options.schemaVersion
 * @param {() => number} [options.now]
 */
export const createSharedFramePersistence = ({
  storage,
  isEnabled,
  schemaVersion,
  now = Date.now,
}) => {
  const digestByScope = new Map()

  const resolveDigest = async (scope) => {
    if (!digestByScope.has(scope)) digestByScope.set(scope, await digestScope(scope))
    return digestByScope.get(scope)
  }

  const buildStoredKey = async (key, scope) => [schemaVersion, await resolveDigest(scope), key].join(KEY_SEPARATOR)

  const isValidRecord = (record) => Boolean(record) &&
    typeof record.valueJson === 'string' && typeof record.timestamp === 'number'

  return {
    async read(key, scope) {
      if (!isEnabled() || !scope) return null
      const storedKey = await buildStoredKey(key, scope)
      const record = await storage.getItem(storedKey)
      if (!isValidRecord(record)) return null
      if (record.timestamp > now()) {
        storage.removeItem(storedKey).catch(() => {})
        return null
      }
      return { value: JSON.parse(record.valueJson), timestamp: record.timestamp }
    },

    async write(key, value, timestamp, scope) {
      if (!isEnabled() || !scope || value === undefined || value === null) return
      // Serialised before the first await: callers may mutate the value right after it resolves.
      const valueJson = JSON.stringify(value)
      const storedKey = await buildStoredKey(key, scope)
      // A sibling frame whose request started later may already have stored a newer body.
      const existing = await storage.getItem(storedKey)
      if (isValidRecord(existing) && existing.timestamp > timestamp) return
      await storage.setItem(storedKey, { valueJson, timestamp })
    },

    async remove(key, scope) {
      if (!scope) return
      await storage.removeItem(await buildStoredKey(key, scope))
    },

    // Ignores the gate and the schema version: copies saved before a flag flip or a schema bump must go too.
    async removeInEveryScope(key) {
      const storedKeys = await storage.keys()
      const suffix = `${KEY_SEPARATOR}${key}`
      await Promise.all(storedKeys.filter((storedKey) => storedKey.endsWith(suffix))
        .map((storedKey) => storage.removeItem(storedKey)))
    },
  }
}

let storage = null
const getStorage = () => {
  if (!storage) {
    storage = localforage.createInstance({ name: 'aha-offline-cache', driver: localforage.INDEXEDDB })
  }
  return storage
}

export const sharedFrameStorage = {
  getItem: (key) => getStorage().getItem(key),
  setItem: (key, value) => getStorage().setItem(key, value),
  removeItem: (key) => getStorage().removeItem(key),
  keys: () => getStorage().keys(),
}
